import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, ButtonBase, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import type { RefillSummarySectionData } from "slices/refillSummarySelectors";
import { REFILL_SUMMARY_FADE_LIMIT } from "utils/designTokens";
import { createSplitDishName } from "utils/utils";
import { OrderListDishTypeItem } from "./OrderListDishTypeItem";
import { StyledDishTypeList } from "./StyledDishTypeList";

export type RefillSummaryVariant = "added" | "removed";

type RefillSummarySectionProps = {
	variant: RefillSummaryVariant;
	section: RefillSummarySectionData;
	expanded: boolean;
	onToggle: () => void;
};

// One accordion of the refill summary: the header carries the piece total and opens the list of dish types.
// A section with nothing in it is the same accordion, so both always look alike; it opens to the empty line.
export const RefillSummarySection = ({ variant, section, expanded, onToggle }: RefillSummarySectionProps) => {
	const { t } = useTranslation();
	const labelKey = variant === "added" ? "order.refillSummary.added" : "order.refillSummary.removed";
	const hasRows = section.rows.length > 0;
	const contentId = `refill-summary-${variant}-list`;

	return (
		<Box
			// An open list takes the height the closed section gives up; a closed section, or an open one with
			// nothing in it, is only as tall as its content.
			flex={expanded && hasRows ? "1 1 0" : "0 0 auto"}
			minHeight={0}
			width="100%"
			display="flex"
			flexDirection="column"
			gap={1}
			data-testid={`refill-summary-${variant}`}
		>
			<SectionToggle
				onClick={onToggle}
				aria-expanded={expanded}
				// The content is rendered only while open: no reference to an element that is not there.
				aria-controls={expanded ? contentId : undefined}
				data-testid={`refill-summary-${variant}-toggle`}
			>
				{/* `data-total` carries the number for tests: the shared i18n mock returns the raw key. */}
				<Typography
					variant="mealMain"
					color="white"
					data-testid={`refill-summary-${variant}-header`}
					data-total={String(section.total)}
				>
					{t(labelKey, { pieces: section.total })}
				</Typography>
				<ExpandMoreIcon sx={{ fontSize: 40, transform: expanded ? "rotate(180deg)" : "none" }} />
			</SectionToggle>
			{expanded && !hasRows && (
				<Typography
					id={contentId}
					data-testid={`refill-summary-${variant}-empty`}
					variant="mealSub"
					color="white"
					// Lined up with the header text inside the toggle.
					sx={{ opacity: 0.6, paddingX: 2 }}
				>
					{t("order.refillSummary.empty")}
				</Typography>
			)}
			{expanded && hasRows && (
				// Rendered only while open. `overflow: auto` (StyledDishTypeList) is what lets the drawer hand a drag
				// to native scrolling instead of swiping itself.
				<StyledDishTypeList
					id={contentId}
					data-testid={contentId}
					// A long list fades out at the bottom, like a large order's: the hint that there is more to scroll.
					isLargeOrder={section.rows.length > REFILL_SUMMARY_FADE_LIMIT}
					sx={{ height: "auto", minHeight: 0, flex: 1 }}
				>
					{/* The order summary's row without the price; a type the terminal does not know gets a name too. */}
					{section.rows.map((row) => (
						<OrderListDishTypeItem
							key={`refill_summary_${variant}_${row.dishTypeId ?? "unknown"}`}
							data-testid="refill-summary-row"
							data-dish-type-id={row.dishTypeId ?? "unknown"}
							name={createSplitDishName(row.name) ?? { header: t("order.refillSummary.unknownDish"), subHeader: "" }}
							pieces={row.pieces}
							dishType={{ id: row.dishTypeId ?? "unknown", image: row.image }}
							showPrice={false}
							// The 84 px order row is fixed-height; a wrapped name plus its subtitle may need more.
							sx={{ height: "auto", minHeight: "84px" }}
						/>
					))}
				</StyledDishTypeList>
			)}
		</Box>
	);
};

const SectionToggle = styled(ButtonBase)(({ theme }) => ({
	width: "100%",
	minHeight: 64,
	flexShrink: 0,
	justifyContent: "space-between",
	padding: theme.spacing(1, 2),
	borderRadius: theme.shape.borderRadius,
	backgroundColor: theme.palette.common.grey[9],
	color: theme.palette.common.white,
	textAlign: "left",
}));
